import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, X, Sparkle, ArrowRight } from "@phosphor-icons/react";
import PublicShell from "@/components/PublicShell";
import { PLANS, BILLING, SITE } from "@/lib/siteInfo";

const ease = [0.22, 1, 0.36, 1];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);

  const priceFor = (plan) => {
    if (plan.id === "free") return { amount: "R0", sub: "forever" };
    const amt = yearly ? BILLING.premiumYearly : BILLING.premiumMonthly;
    return { amount: `${BILLING.currency}${amt}`, sub: yearly ? "per year" : "per month" };
  };

  return (
    <PublicShell testId="pricing-page">
      <section className="px-6 pt-16 pb-10 text-center">
        <div className="max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-riq-primary/20 bg-white/70 px-4 py-1.5 text-xs font-semibold text-riq-primary">
            <Sparkle size={14} weight="fill" /> Simple, honest pricing
          </div>
          <h1 className="font-head font-black tracking-tighter mt-6 text-4xl sm:text-5xl">
            Start free. Upgrade when you're ready.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            No card needed to begin. Prices are shown in South African Rand (ZAR) and include VAT where applicable.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center gap-3 rounded-full bg-white border border-riq-border p-1 shadow-sm" data-testid="billing-toggle">
            <button data-testid="billing-monthly" onClick={() => setYearly(false)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${!yearly ? "bg-riq-primary text-white" : "text-slate-500 hover:text-slate-800"}`}>
              Monthly
            </button>
            <button data-testid="billing-yearly" onClick={() => setYearly(true)}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${yearly ? "bg-riq-primary text-white" : "text-slate-500 hover:text-slate-800"}`}>
              Yearly <span className="text-riq-gold">· save ~2 months</span>
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto grid gap-6 md:grid-cols-2 items-start">
          {PLANS.map((plan, i) => {
            const p = priceFor(plan);
            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.1 }}
                data-testid={`plan-${plan.id}`}
                className={`relative rounded-3xl border p-8 shadow-[0_8px_32px_rgba(0,0,0,0.05)] ${
                  plan.highlight ? "border-riq-primary bg-white ring-2 ring-riq-primary/30" : "border-riq-border bg-white"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-8 rounded-full bg-riq-primary text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide shadow">
                    Most popular
                  </span>
                )}
                <h2 className="font-head text-2xl font-bold">{plan.name}</h2>
                <p className="mt-1 text-sm text-slate-500">{plan.tagline}</p>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="font-head text-4xl font-black text-riq-primary">{p.amount}</span>
                  <span className="text-sm text-slate-500">{p.sub}</span>
                </div>

                <Link
                  to="/register"
                  data-testid={`plan-cta-${plan.id}`}
                  className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-transform hover:-translate-y-0.5 active:scale-95 ${
                    plan.highlight ? "bg-riq-primary text-white shadow-lg shadow-riq-primary/25" : "border-2 border-riq-primary/30 text-riq-primary"
                  }`}
                >
                  {plan.cta} <ArrowRight size={18} weight="bold" />
                </Link>

                <ul className="mt-7 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f.t} className="flex items-start gap-3 text-sm">
                      {f.on ? (
                        <CheckCircle size={20} weight="fill" className="text-riq-primary shrink-0 mt-px" />
                      ) : (
                        <X size={20} weight="bold" className="text-slate-300 shrink-0 mt-px" />
                      )}
                      <span className={f.on ? "text-slate-700" : "text-slate-400 line-through"}>{f.t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>

        <p className="max-w-2xl mx-auto mt-10 text-center text-sm text-slate-500">
          Questions about billing? See our{" "}
          <Link to="/refunds" className="text-riq-primary font-semibold hover:underline">Refund &amp; Cancellation policy</Link>{" "}
          or email{" "}
          <a href={`mailto:${SITE.email}`} className="text-riq-primary font-semibold hover:underline">{SITE.email}</a>.
        </p>
      </section>
    </PublicShell>
  );
}
